import { Injectable } from '@angular/core';
import { CanvasService } from './canvas.service';
import { text2xml, walkTree } from '@svgeditor/utilities.js';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { SelectedElementsService } from './selected-elements.service';

@Injectable({ providedIn: 'root' })
export class ImportImageService {
    imagefile: string;

    constructor(
        private canvasService: CanvasService,
        private inputMode: CanvasInputModeService,
        private selectionService: SelectedElementsService
    ) {}

    onSetImage(event) {
        const self = this;
        const canvas = this.canvasService.getCanvas();
        if (event.target.files) {
            this.imagefile = 'assets/images/' + event.target.files[0].name;
            this.getBase64Image(event.target.files[0], function (imgdata) {
                if (canvas.setGoodImage) {
                    canvas.setGoodImage(imgdata);

                    const image = new Image();
                    image.src = imgdata;

                    image.onload = function () {
                        let w = image.width;
                        let h = image.height;
                        if (image.width > canvas.getWidth()) {
                            const ratio = canvas.getWidth() / w;
                            w = w * ratio;
                            h = h * ratio;
                        }
                        if (h > canvas.getHeight()) {
                            const ratio = canvas.getHeight() / h;
                            w = w * ratio;
                            h = h * ratio;
                        }

                        const x = canvas.getWidth() / 2 - w / 2;
                        const y = canvas.getHeight() / 2 - h / 2;

                        const newImage = canvas.addSVGElementsFromJson({
                            element: 'image',
                            attr: {
                                x: x + 'px',
                                y: y + 'px',
                                width: w + 'px',
                                height: h + 'px',
                                id: canvas.getNextId(),
                                opacity: 1,
                                style: 'pointer-events:inherit',
                            },
                        });
                        canvas.setHref(newImage, canvas.getLastGoodImgUrl());

                        const cmd = new canvas.history.InsertElementCommand(newImage);
                        canvas.addCommandToHistory(cmd);
                        self.selectionService.setSelectedElements([newImage]);
                        self.inputMode.setMode(CanvasInputMode.SELECT);
                    };
                }
            });
        }
    }

    onSetSvg(event) {
        const self = this;
        if (event.target.files) {
            this.imagefile = 'assets/images/' + event.target.files[0].name;
            const reader = new FileReader();
            reader.onloadend = function (e: any) {
                self.importSvgString(e.target.result);
                self.inputMode.setMode(CanvasInputMode.SELECT);
            };
            reader.readAsText(event.target.files[0]);
        }
    }

    importSvgString = (data: string) => {
        const canvas = this.canvasService.getCanvas();
        let elementID;
        const nodes = text2xml(data);
        canvas.prepareSvg(nodes);
        const adopted = document.adoptNode(nodes.children[0]);
        canvas.uniquifyElems(adopted, true);

        walkTree(adopted, el => {
            if (el.id === '') {
                el.id = canvas.getNextId();
            }

            elementID = el.id;
        });

        const group = canvas.addSVGElementsFromJson({
            element: 'g',
            attr: {
                id: canvas.getNextId(),
            },
        });

        if (!group) {
            console.error('Failed to create SVG group.');
            adopted.remove();
            return;
        }

        for (let i = adopted.children.length - 1; i >= 0; i--) {
            const childElement = adopted.children[i] as HTMLElement;

            if (childElement.style?.length > 0) {
                for (let j = 0; j < childElement.style.length; j++) {
                    const styleName = childElement.style[j];
                    const styleValue = childElement.style.getPropertyValue(styleName);

                    childElement.setAttribute(styleName, styleValue);
                }

                for (let j = childElement.style.length - 1; j >= 0; j--) {
                    const styleName = childElement.style[j];

                    if (styleName) {
                        childElement.style.removeProperty(styleName);
                    }
                }
            }

            if (childElement.tagName === 'path') {
                const stroke = childElement.getAttribute('stroke');
                const parentStroke = adopted.getAttribute('stroke');

                if (!stroke && parentStroke) {
                    childElement.setAttribute('stroke', parentStroke);
                } else if (!stroke) {
                    childElement.setAttribute('stroke', '#000000');
                }
            }
            const child = childElement.cloneNode(true) as HTMLElement;
            group.prepend(child);
        }

        group.setAttribute('type', CanvasInputMode.ADD_SVG);

        const cmd = new canvas.history.InsertElementCommand(group);
        canvas.addCommandToHistory(cmd);

        this.selectionService.setSelectedElements([group]);
        adopted.remove();
        return elementID;
    };

    /**
     * convert image file to code to attach in svg
     * @param file image file
     * @param callback event for end load image
     */
    private getBase64Image(file, callback) {
        const fr = new FileReader();
        fr.onload = function () {
            callback(fr.result);
        };
        fr.readAsDataURL(file);
    }
}
